/**
 * Routes every theme chat entry point to the provider available in Sofia.
 *
 * LiveChat owns 09:00 (inclusive) to 18:00 (exclusive). Zipchat owns the
 * remaining hours. Zipchat AI Search stays available independently; only its
 * chat bubble and chat iframe are hidden while LiveChat is active.
 */
(function (root, factory) {
  const router = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = router;
    return;
  }

  root.CPAPChatRouter = router;
  router.init();
})(typeof window === 'undefined' ? globalThis : window, function () {
  const SOFIA_TIME_ZONE = 'Europe/Sofia';
  const LIVECHAT_START_MINUTES = 9 * 60;
  const LIVECHAT_END_MINUTES = 18 * 60;
  const LIVECHAT_SCRIPT_ID = 'deferred-livechat-widget';
  const LIVECHAT_SRC = 'https://shopify.livechatinc.com/api/v1/script/shop/cpapeuropacom.myshopify.com/widget.js';
  const CHAT_TRIGGERS = [
    '[data-livechat-open]',
    '[data-open-livechat]',
    '.js-open-livechat',
    'a[href="#open-livechat"]',
    'a[href="#livechat"]',
    'a[href="livechat:open"]',
    'a[href="https://chat-with-us"]'
  ].join(',');
  const clockFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: SOFIA_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  });

  let currentProvider = null;
  let liveChatRequested = false;
  let hasInteracted = false;
  let minuteTimer = null;
  let observedZipchatRoot = null;
  const originalDisplay = new WeakMap();
  const documentObserver = typeof MutationObserver === 'function'
    ? new MutationObserver(syncProviderElements)
    : null;
  const zipchatObserver = typeof MutationObserver === 'function'
    ? new MutationObserver(syncProviderElements)
    : null;

  function getSofiaMinutes(date) {
    const parts = clockFormatter.formatToParts(date);
    const values = {};

    parts.forEach((part) => {
      if (part.type !== 'literal') values[part.type] = Number.parseInt(part.value, 10);
    });

    return (values.hour * 60) + values.minute;
  }

  function getProviderAt(date) {
    const minutes = getSofiaMinutes(date);
    return minutes >= LIVECHAT_START_MINUTES && minutes < LIVECHAT_END_MINUTES
      ? 'livechat'
      : 'zipchat';
  }

  function getPreviewOverride() {
    const themeRole = window.Shopify?.theme?.role;
    if (!themeRole || themeRole === 'main') return null;

    const provider = new URLSearchParams(window.location.search).get('chat_provider');
    return provider === 'livechat' || provider === 'zipchat' ? provider : null;
  }

  function setRouterHidden(element, hidden) {
    if (!element) return;

    if (hidden) {
      if (!originalDisplay.has(element)) {
        originalDisplay.set(element, {
          value: element.style.getPropertyValue('display'),
          priority: element.style.getPropertyPriority('display')
        });
      }
      element.style.setProperty('display', 'none', 'important');
      return;
    }

    const display = originalDisplay.get(element);
    if (!display) return;

    if (display.value) {
      element.style.setProperty('display', display.value, display.priority);
    } else {
      element.style.removeProperty('display');
    }
    originalDisplay.delete(element);
  }

  function bindZipchatObserver() {
    const root = document.getElementById('zipchat-shadow-host')?.shadowRoot;
    if (!root || root === observedZipchatRoot || !zipchatObserver) return root;

    observedZipchatRoot = root;
    zipchatObserver.disconnect();
    zipchatObserver.observe(root, { childList: true, subtree: true });
    return root;
  }

  function syncProviderElements() {
    if (!currentProvider) return;

    const zipchatRoot = bindZipchatObserver();
    const zipchatBubble = zipchatRoot?.getElementById('widget-chat-container');
    const zipchatIframe = document.getElementById('zipchat-iframe');
    const liveChatContainer = document.getElementById('chat-widget-container');
    const liveChatActive = currentProvider === 'livechat';

    setRouterHidden(zipchatBubble, liveChatActive);
    setRouterHidden(zipchatIframe, liveChatActive);
    setRouterHidden(liveChatContainer, !liveChatActive);
  }

  function loadLiveChat() {
    if (currentProvider !== 'livechat') return Promise.resolve(false);
    if (window.LiveChatWidget || document.getElementById(LIVECHAT_SCRIPT_ID)) {
      return Promise.resolve(true);
    }
    if (liveChatRequested) return Promise.resolve(true);

    liveChatRequested = true;

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.id = LIVECHAT_SCRIPT_ID;
      script.src = LIVECHAT_SRC;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => {
        liveChatRequested = false;
        script.remove();
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  function openLiveChat(attempt = 0) {
    if (currentProvider !== 'livechat') return;

    loadLiveChat().then(() => {
      if (currentProvider !== 'livechat') return;

      if (window.LiveChatWidget && typeof window.LiveChatWidget.call === 'function') {
        window.LiveChatWidget.call('maximize');
        return;
      }

      if (attempt < 40) {
        window.setTimeout(() => openLiveChat(attempt + 1), 250);
      }
    });
  }

  function openZipchat(attempt = 0) {
    if (currentProvider !== 'zipchat') return;

    syncProviderElements();

    if (window.Zipchat && typeof window.Zipchat.open === 'function') {
      if (window.Zipchat.open({ open: true }) !== false) return;
    }

    if (attempt < 40) {
      window.setTimeout(() => openZipchat(attempt + 1), 250);
    }
  }

  function openCurrentChat() {
    if (currentProvider === 'livechat') {
      openLiveChat();
      return;
    }

    openZipchat();
  }

  function applyProvider() {
    const nextProvider = getPreviewOverride() || getProviderAt(new Date());
    const changed = nextProvider !== currentProvider;
    currentProvider = nextProvider;
    document.documentElement.dataset.chatProvider = currentProvider;
    syncProviderElements();

    if (!changed) return;

    if (currentProvider === 'zipchat') {
      if (window.LiveChatWidget && typeof window.LiveChatWidget.call === 'function') {
        window.LiveChatWidget.call('hide');
      }
      return;
    }

    if (hasInteracted) loadLiveChat();
  }

  function scheduleNextMinute() {
    window.clearTimeout(minuteTimer);
    const delay = 60000 - (Date.now() % 60000) + 50;

    minuteTimer = window.setTimeout(() => {
      applyProvider();
      scheduleNextMinute();
    }, delay);
  }

  function handleFirstInteraction() {
    hasInteracted = true;
    if (currentProvider === 'livechat') loadLiveChat();
  }

  function init() {
    applyProvider();

    if (documentObserver) {
      documentObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    document.addEventListener('click', (event) => {
      const target = event.target.closest?.(CHAT_TRIGGERS);
      if (!target) return;

      event.preventDefault();
      openCurrentChat();
    });

    ['pointerdown', 'touchstart', 'keydown', 'scroll'].forEach((eventName) => {
      window.addEventListener(eventName, handleFirstInteraction, { passive: true });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') applyProvider();
    });
    window.addEventListener('pageshow', applyProvider);
    scheduleNextMinute();
  }

  return {
    getProviderAt,
    init,
    open: openCurrentChat
  };
});
